import type { StreakInfo } from "../../hooks/useProgressReport";

export interface StreakCardProps {
  streakInfo: StreakInfo;
}

/**
 * Streak information card showing current and longest streaks.
 * Features fire emoji animations for active streaks.
 */
export function StreakCard({ streakInfo }: StreakCardProps) {
  const { currentStreak, longestStreak, streakMaintained } = streakInfo;
  const hasStreak = currentStreak > 0;
  const isNewRecord = currentStreak === longestStreak && currentStreak > 1;

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">🔥</span>
        Streak
      </h2>

      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-sunshine-100 via-sunshine-50 to-fairy-50 border border-sunshine-200/50 p-5">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-24 h-24 bg-fairy-200/30 rounded-full blur-xl transform translate-x-8 -translate-y-8 print:hidden" />

        <div className="relative z-10 flex items-center justify-between">
          {/* Current streak */}
          <div className="flex items-center gap-3">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center ${
                hasStreak
                  ? "bg-gradient-to-br from-sunshine-400 to-fairy-400 animate-pulse"
                  : "bg-neutral-200"
              }`}
              style={{ animationDuration: "2s" }}
            >
              <span className="text-2xl" role="img" aria-label="Streak">
                {hasStreak ? "🔥" : "💤"}
              </span>
            </div>
            <div>
              <p className="font-display font-bold text-3xl text-neutral-800">
                {currentStreak}
                <span className="text-lg font-semibold text-neutral-600 ml-1">
                  day{currentStreak !== 1 ? "s" : ""}
                </span>
              </p>
              <p className="font-body text-sm text-neutral-600">
                {hasStreak ? "Current streak" : "Start a new streak!"}
              </p>
            </div>
          </div>

          {/* Longest streak */}
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end">
              <span className="text-lg">🏆</span>
              <p className="font-display font-bold text-2xl text-magic-600">
                {longestStreak}
              </p>
            </div>
            <p className="font-body text-sm text-neutral-500">Best streak</p>
          </div>
        </div>

        {/* Status messages */}
        <div className="mt-4 pt-4 border-t border-sunshine-200/50">
          {isNewRecord ? (
            <div className="flex items-center gap-2 text-sunshine-700 bg-sunshine-200/50 rounded-lg px-3 py-2">
              <span className="text-lg">🎉</span>
              <span className="font-body text-sm font-medium">
                New personal record!
              </span>
            </div>
          ) : streakMaintained ? (
            <div className="flex items-center gap-2 text-meadow-700 bg-meadow-100/50 rounded-lg px-3 py-2">
              <span className="text-lg">✅</span>
              <span className="font-body text-sm font-medium">
                Streak maintained this period!
              </span>
            </div>
          ) : hasStreak ? (
            <div className="flex items-center gap-2 text-neutral-600 bg-neutral-100 rounded-lg px-3 py-2">
              <span className="text-lg">💪</span>
              <span className="font-body text-sm">
                Keep playing daily to grow the streak!
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-neutral-500 bg-neutral-100 rounded-lg px-3 py-2">
              <span className="text-lg">💡</span>
              <span className="font-body text-sm">
                Play today to start a new streak!
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StreakCard;
